import { useEffect, useState, useCallback } from 'react';
import * as Sentry from "@sentry/react"
interface Submission {
  id: number;
  company: string;
  role: string;
  country: string;
  max_spend: number | null;
  unlimited: number;
  actual_spend: number | null;
  providers: string;
  verified: number;
  created_at: string;
}

interface TokenTableProps {
  refreshKey: number;
}

function formatSpend(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M/mo`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K/mo`;
  return `$${amount.toLocaleString()}/mo`;
}

function parseProviders(raw: string): string[] {
  return raw ? raw.split(',').filter(Boolean) : [];
}

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI', anthropic: 'Anthropic', github: 'GitHub Copilot',
  google: 'Google', alibaba: 'Alibaba', deepseek: 'DeepSeek',
  mistral: 'Mistral', perplexity: 'Perplexity', cohere: 'Cohere',
  meta: 'Meta (Llama)',
};

const COUNTRY_FLAG: Record<string, string> = {
  'US': '🇺🇸', 'GB': '🇬🇧', 'DE': '🇩🇪', 'FR': '🇫🇷', 'CA': '🇨🇦',
  'AU': '🇦🇺', 'JP': '🇯🇵', 'KR': '🇰🇷', 'CN': '🇨🇳', 'IN': '🇮🇳',
  'SG': '🇸🇬', 'IL': '🇮🇱', 'NL': '🇳🇱', 'SE': '🇸🇪', 'CH': '🇨🇭',
  'IE': '🇮🇪', 'BR': '🇧🇷', 'AE': '🇦🇪', 'HK': '🇭🇰', 'ES': '🇪🇸',
  'IT': '🇮🇹', 'PL': '🇵🇱', 'MX': '🇲🇽',
};

import { COUNTRIES, JOB_TITLES, COUNTRY_NAME } from '../constants';

export default function TokenTable({ refreshKey }: TokenTableProps) {
  const [data, setData] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [searchCompany, setSearchCompany] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterRole, setFilterRole] = useState('');

  const fetchData = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (searchCompany.trim()) params.set('company', searchCompany.trim());
    if (filterCountry) params.set('country', filterCountry);
    if (filterRole) params.set('role', filterRole);
    const qs = params.toString();

    fetch(`/api/submissions${qs ? `?${qs}` : ''}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to load data');
        return res.json();
      })
      .then((payload: any) => {
        // Support both old array format and new { data, meta } format
        const rows = Array.isArray(payload) ? payload : (payload.data || []);
        setData(rows);
        setError(null);
      })
      .catch((err) => {
        setError(err.message)
        Sentry.captureException(err);
      })
      .finally(() => setLoading(false));
  }, [searchCompany, filterCountry, filterRole]);

  // Fetch on mount, on refreshKey change, and on filter change (debounced for search)
  useEffect(() => {
    fetchData();
  }, [refreshKey, filterCountry, filterRole]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => fetchData(), 350);
    return () => clearTimeout(timer);
  }, [searchCompany]); // eslint-disable-line react-hooks/exhaustive-deps

  // Collect unique countries from data for the dropdown
  const availableCountries = COUNTRIES.filter(
    (c) => !filterCountry || c.code === filterCountry
  );

  return (
    <section id="leaderboard" className="container leaderboard animate-fade-in" style={{ animationDelay: '0.2s' }}>
      <div className="section-heading">
        <div><h2>Spend benchmark</h2><p>A living view of how teams invest in AI.</p></div>
        <span className="badge badge-indigo">{data.length} submissions</span>
      </div>

      {/* ═══ FILTER BAR ═══ */}
      <div className="glass-panel filters" style={{
        display: 'flex', gap: '1rem', padding: '1rem 1.25rem',
        marginBottom: '1.5rem', alignItems: 'center', flexWrap: 'wrap',
      }}>
        {/* Search */}
        <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
          <span style={{
            position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)',
            color: 'var(--text-secondary)', pointerEvents: 'none', fontSize: '0.9rem',
          }}>🔍</span>
          <input
            id="filter-company"
            type="text"
            className="input-field"
            placeholder="Search company…"
            value={searchCompany}
            onChange={(e) => setSearchCompany(e.target.value)}
            style={{ paddingLeft: '2.25rem', padding: '0.6rem 1rem 0.6rem 2.25rem', fontSize: '0.9rem' }}
          />
        </div>

        {/* Country dropdown */}
        <div style={{ flex: '0 1 200px', minWidth: '150px' }}>
          <select
            id="filter-country"
            className="input-field"
            value={filterCountry}
            onChange={(e) => setFilterCountry(e.target.value)}
            style={{ padding: '0.6rem 1rem', fontSize: '0.9rem' }}
          >
            <option value="">🌍 All Countries</option>
            {availableCountries.map((c) => (
              <option key={c.code} value={c.code}>
                {COUNTRY_FLAG[c.code] || ''} {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Job title dropdown */}
        <div style={{ flex: '0 1 220px', minWidth: '160px' }}>
          <select
            id="filter-role"
            className="input-field"
            value={filterRole}
            onChange={(e) => setFilterRole(e.target.value)}
            style={{ padding: '0.6rem 1rem', fontSize: '0.9rem' }}
          >
            <option value="">💼 All Roles</option>
            {JOB_TITLES.map((title) => (
              <option key={title} value={title}>{title}</option>
            ))}
          </select>
        </div>

        {/* Clear filters */}
        {(searchCompany || filterCountry || filterRole) && (
          <button
            className="btn btn-secondary"
            style={{ padding: '0.5rem 1rem', fontSize: '0.8rem', whiteSpace: 'nowrap' }}
            onClick={() => { setSearchCompany(''); setFilterCountry(''); setFilterRole(''); }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* ═══ TABLE ═══ */}
      {loading && (
        <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-secondary)' }}>Loading submissions…</p>
      )}

      {error && (
        <p style={{ textAlign: 'center', padding: '3rem', color: 'var(--accent-secondary)' }}>Error: {error}</p>
      )}

      {!loading && !error && data.length === 0 && (
        <div className="glass-panel" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
          <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
            {searchCompany || filterCountry ? 'No results match your filters' : 'No submissions yet'}
          </p>
          <p>
            {searchCompany || filterCountry
              ? 'Try adjusting your search or clearing filters.'
              : 'Be the first to share your company\'s AI token spend!'}
          </p>
        </div>
      )}

      {!loading && !error && data.length > 0 && (
        <div className="data-table-wrapper glass-panel">
          <table className="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Company</th>
                <th>Country</th>
                <th>Role</th>
                <th>Max Budget / Mo</th>
                <th>Actual Spend / Mo</th>
                <th>Approved Providers</th>
                <th className="verified-column">Verified</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, index) => {
                const providers = parseProviders(row.providers);
                const utilization = row.actual_spend != null && row.max_spend != null && row.max_spend > 0
                  ? Math.round((row.actual_spend / row.max_spend) * 100)
                  : null;
                const flag = COUNTRY_FLAG[row.country] || '';
                const countryName = COUNTRY_NAME[row.country] || row.country;
                return (
                  <tr key={row.id}>
                    <td style={{ color: 'var(--text-secondary)', fontWeight: 600 }}>{index + 1}</td>
                    <td style={{ fontWeight: 600 }}>{row.company}</td>
                    <td className="verified-column">
                      {row.country ? (
                        <span title={countryName} style={{ whiteSpace: 'nowrap' }}>
                          {flag} <span style={{ color: 'var(--text-secondary)', fontSize: '0.85rem' }}>{countryName}</span>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-secondary)' }}>{row.role}</td>
                    <td style={{ color: 'var(--accent-secondary)', fontWeight: 700 }}>
                      {row.unlimited ? 'Unlimited' : (row.max_spend != null ? formatSpend(row.max_spend) : '—')}
                    </td>
                    <td>
                      {row.actual_spend != null ? (
                        <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: 600 }}>{formatSpend(row.actual_spend)}</span>
                          {utilization != null && (
                            <span className="badge" style={{
                              backgroundColor: utilization > 80 ? 'rgba(199,80,80,0.1)' : 'rgba(45,157,111,0.1)',
                              color: utilization > 80 ? '#C75050' : '#2D9D6F',
                              fontSize: '0.7rem',
                            }}>
                              {utilization}%
                            </span>
                          )}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                        {providers.length > 0
                          ? providers.map((p) => (
                            <span key={p} className="badge badge-indigo" style={{ fontSize: '0.7rem' }}>
                              {PROVIDER_LABELS[p] || p}
                            </span>
                          ))
                          : <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>—</span>
                        }
                      </div>
                    </td>
                    <td>
                      {row.verified ? (
                        <span className="badge" style={{
                          backgroundColor: 'rgba(45,157,111,0.1)',
                          color: '#2D9D6F', fontSize: '0.7rem',
                          gap: '0.25rem', display: 'inline-flex', alignItems: 'center',
                        }}>
                          ✓ Verified
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.875rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
